import React from "react"
import { graphql, navigate } from "gatsby"

import {
  ArticleBlockText,
  ArticleBlockImage,
  ArticleBlockQuote,
  ArticleBlockEmbedCode,
} from "./blocks"

import ArticleRelatedContent from "./related-content"

import {
  ArticleFieldsFragment,
  ArticleContentTextFieldsFragment,
  ArticleContentImageFieldsFragment,
  ArticleContentQuoteFieldsFragment,
  ArticleTeaserFieldsFragment,
  ArticleContentEmbedCodeFieldsFragment,
} from "../../../graphql-types"

import ArticlesTeaser from "../articles-teaser"

export const query = graphql`
  fragment ArticleFields on DatoCmsArticle {
    key
    title
    articleType
    publishedDate(formatString: "MMMM DD, YYYY")
    categories {
      id
      name
    }
    preambleNode {
      childMarkdownRemark {
        html
      }
    }
    articleContent {
      ... on DatoCmsArticleBlockText {
        ...ArticleContentTextFields
      }
      ... on DatoCmsArticleBlockImage {
        ...ArticleContentImageFields
      }
      ... on DatoCmsArticleBlockQuote {
        ...ArticleContentQuoteFields
      }
      ... on DatoCmsArticleBlockEmbedCode {
        ...ArticleContentEmbedCodeFields
      }
    }
    relatedContent {
      ... on DatoCmsRelatedContentAsset {
        ...ArticleRelatedContentAssetFields
      }
      ... on DatoCmsRelatedContentLink {
        ...ArticleRelatedContentLinkFields
      }
    }
    seoMetaTags {
      ...GatsbyDatoCmsSeoMetaTags
    }
    metaTags {
      description
    }
  }
`

const Article: React.FC<{
  data: ArticleFieldsFragment
  articles: ArticleTeaserFieldsFragment[]
}> = ({ data, articles }) => {
  const category =
    data.categories && data.categories.length > 0 ? data.categories[0].name : ""

  const goBack = () => {
    const path = data.articleType === "Stories" ? "/stories/" : "/news/"
    navigate(path)
  }

  return (
    <>
      <div className="o-wrapper o-row">
        <article className="c-article">
          <header className="c-article__header -green">
            <div className="o-wrapper">
              {category && (
                <div className="c-article__label">
                  {category}
                  <button className="c-article__prev" onClick={goBack}></button>
                </div>
              )}
              <h1 className="c-article__title">{data.title}</h1>
              <p className="c-article__date">{data.publishedDate}</p>
            </div>
          </header>
          {data.preambleNode && data.preambleNode.childMarkdownRemark && (
            <div
              className="c-article__preamble"
              dangerouslySetInnerHTML={{
                __html: data.preambleNode.childMarkdownRemark.html,
              }}
            />
          )}
          {data.articleContent &&
            data.articleContent.map(block => {
              if (block.model && block.model.apiKey) {
                switch (block.model.apiKey) {
                  case "article_block_text":
                    return (
                      <ArticleBlockText
                        key={block.id}
                        {...(block as ArticleContentTextFieldsFragment)}
                      />
                    )
                  case "article_block_quote":
                    return (
                      <ArticleBlockQuote
                        key={block.id}
                        {...(block as ArticleContentQuoteFieldsFragment)}
                      />
                    )
                  case "article_block_image":
                    return (
                      <ArticleBlockImage
                        key={block.id}
                        {...(block as ArticleContentImageFieldsFragment)}
                      />
                    )
                  case "article_block_embed_code":
                    return (
                      <ArticleBlockEmbedCode
                        key={block.id}
                        {...(block as ArticleContentEmbedCodeFieldsFragment)}
                      />
                    )
                }
              }
            })}
          {data.relatedContent.length > 0 && (
            <ArticleRelatedContent relatedContent={data.relatedContent} />
          )}
        </article>
      </div>
      <div className="o-wrapper o-row">
        <ArticlesTeaser
          title={data.articleType === "Stories" ? "More stories" : "More news"}
          componentId="article-articles-teaser"
          categories={data.categories}
          articles={articles.filter(a => a.key !== data.key)}
        ></ArticlesTeaser>
      </div>
    </>
  )
}

export default Article
